import { useState, useEffect, useMemo } from 'react';
import type { Context } from 'types/context';
import type { UseCaseRow } from 'types/schema';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  composeContextFilter,
  computeLibraryStats,
  type ContextLibraryFilters,
  type ContextLibraryStats,
} from '../utils/contextClassification';

export interface UseContextLibraryResult {
  filteredContexts: Context[];
  useCases: UseCaseRow[];
  useCasesLoading: boolean;
  filters: ContextLibraryFilters;
  setFilters: (filters: ContextLibraryFilters) => void;
  stats: ContextLibraryStats;
}

export function useContextLibrary(
  contexts: Context[],
  locale: string
): UseContextLibraryResult {
  const apiClients = useApiClientsOrNull();

  const [filters, setFilters] = useState<ContextLibraryFilters>({
    source: 'all',
    search: '',
    useCaseTags: [],
  });

  const [useCases, setUseCases] = useState<UseCaseRow[]>([]);
  const [useCasesLoading, setUseCasesLoading] = useState(false);

  useEffect(() => {
    if (!apiClients?.schemaClient) return;

    let cancelled = false;
    setUseCasesLoading(true);

    const params = new URLSearchParams();
    params.append('locale', locale);

    apiClients.schemaClient
      .get<UseCaseRow[]>(`/use-cases?${params.toString()}`)
      .then((data) => {
        if (!cancelled) {
          setUseCases(data ?? []);
          setUseCasesLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUseCasesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiClients, locale]);

  const filteredContexts = useMemo(
    () => composeContextFilter(contexts, filters, locale),
    [contexts, filters, locale]
  );

  const stats = useMemo(() => computeLibraryStats(contexts), [contexts]);

  return { filteredContexts, useCases, useCasesLoading, filters, setFilters, stats };
}
